import type { ChartData, ChartDataset } from "chart.js";

import { DOMAIN_SCORE_CONFIG } from "../assessmentConfig";
import { type AssessmentRecord, type AssessmentType } from "../types";
import { formatAssessmentChartDateLabel, getMaxAndMin, updateMonths } from "../utils";

interface AssessmentChartPayload {
  hasData: boolean;
  data: ChartData<"line" | "bar">;
  min: number;
  max: number;
  /** Populated for mixed charts: tight bounds around bar (y1) datasets. */
  barMin?: number;
  barMax?: number;
}

function formatLabels(dataAssessments: AssessmentRecord[]) {
  if (!dataAssessments.length) {
    return [];
  }

  const initDate = updateMonths(dataAssessments[0].data.administration_date, 6, false);
  const endDate = updateMonths(
    dataAssessments[dataAssessments.length - 1].data.administration_date,
    6,
    true
  );

  return [
    formatAssessmentChartDateLabel(initDate),
    ...dataAssessments.map((assessment) =>
      formatAssessmentChartDateLabel(assessment.data.administration_date)
    ),
    formatAssessmentChartDateLabel(endDate),
  ];
}

function buildVinelandData(dataAssessments: AssessmentRecord[]) {
  const labels = formatLabels(dataAssessments);
  const valuesY: number[] = [];
  const domainValues = DOMAIN_SCORE_CONFIG.map(() => [null] as Array<number | null>);

  dataAssessments.forEach((assessment) => {
    DOMAIN_SCORE_CONFIG.forEach((score, index) => {
      const value = assessment.data[score.key];
      domainValues[index].push(typeof value === "number" ? value : null);

      if (typeof value === "number") {
        valuesY.push(value);
      }
    });
  });

  DOMAIN_SCORE_CONFIG.forEach((_, index) => {
    domainValues[index].push(null);
  });

  const data: ChartData<"line"> = {
    labels,
    datasets: DOMAIN_SCORE_CONFIG.map((score, index) => ({
      label: score.graphLabel,
      borderColor: score.borderColor,
      backgroundColor: score.backgroundColor,
      data: domainValues[index],
    })),
  };

  return { data, valuesY };
}

/**
 * Builds chart payload consumed by the generic ANChartJSWrapperV2 component.
 * Keeps existing assessment data mapping behavior while delegating rendering/tooltips/legend to generics.
 */
export function buildAssessmentChartPayload(
  assessmentType: AssessmentType,
  dataAssessments: AssessmentRecord[],
  configuredDatasets: ChartDataset<"line" | "bar", Array<number | null>>[]
): AssessmentChartPayload {
  if (!dataAssessments.length) {
    return {
      hasData: false,
      data: { labels: [], datasets: [] },
      min: 0,
      max: 100,
    };
  }

  if (assessmentType === "vineland") {
    const { data, valuesY } = buildVinelandData(dataAssessments);
    const { max, min } = getMaxAndMin(valuesY);

    return {
      hasData: true,
      data,
      min,
      max,
    };
  }

  const labels = dataAssessments.map((assessment) =>
    formatAssessmentChartDateLabel(assessment.data.administration_date)
  );
  const datasets = configuredDatasets.map((dataset) => {
    const dataKey = (dataset as ChartDataset<"line" | "bar"> & { dataKey?: string }).dataKey;
    const data = dataAssessments.map((assessment) => {
      if (dataKey) {
        const value = assessment.data[dataKey];
        return typeof value === "number" ? value : null;
      }

      const fallback = Object.values(assessment.data).find((value) => typeof value === "number") as
        | number
        | undefined;

      return typeof fallback === "number" ? fallback : null;
    });

    return {
      ...dataset,
      data,
    };
  }) as ChartDataset<"line" | "bar", Array<number | null>>[];

  const isBar = (ds: ChartDataset<"line" | "bar", Array<number | null>>) =>
    (ds as ChartDataset<"line" | "bar"> & { yAxisID?: string }).yAxisID === "y1";

  const lineValues = datasets
    .filter((ds) => !isBar(ds))
    .flatMap((ds) => ds.data)
    .filter((v): v is number => typeof v === "number");

  const barValues = datasets
    .filter((ds) => isBar(ds))
    .flatMap((ds) => ds.data)
    .filter((v): v is number => typeof v === "number");

  const hasMixedAxes = barValues.length > 0 && lineValues.length > 0;

  const allValues = hasMixedAxes ? lineValues : [...lineValues, ...barValues];
  const { max, min } = getMaxAndMin(allValues.length ? allValues : [...lineValues, ...barValues]);

  const barBounds = hasMixedAxes ? getMaxAndMin(barValues) : undefined;

  return {
    hasData: [...lineValues, ...barValues].length > 0,
    data: {
      labels,
      datasets,
    },
    min,
    max,
    barMin: barBounds?.min,
    barMax: barBounds?.max,
  };
}
